import PostalAddress, { addressFormats } from 'i18n-postal-address';
import { GetAWorkplaceResponse } from '@thera-hr/check-api-sdk/api/types';

// import { getStateDetailsByIso2 } from 'services/countryStateCityApi';
import { IAddress } from 'types';
import { logError } from './error';
import { countryList } from 'utils/country';

type AddressType = IAddress & { type?: 'business' | 'personal'; toArray?: boolean }; // | 'english' | 'default' | 'french'

export const displayAddress = (props: AddressType) => {
  // v1 no longer loads country formats by default — pass them all so any country keeps working
  const myAddress = new PostalAddress({ formats: addressFormats, defaultFormat: 'US' });
  const { city, state, country, postalCode, line1, line2 = '', type = 'business', toArray = false } = props || {};
  try {
    const countryName = countryList[country]?.name;
    if (!countryName) {
      logError(new Error(`Country name not found for ${country} in [countryList]`));
    }
    const address = myAddress.setCountry(country).setCity(city).setPostalCode(postalCode).setAddress1(line1).setFormat({
      country,
      type,
    });

    if (line2) {
      address.setAddress2(line2);
    } else {
      // setting this because the library holds reference to the previous value
      address.setAddress2('');
    }

    if (state) {
      address.setState(state);
    } else {
      // setting this because the library holds reference to the previous value
      address.setState('');
    }

    if (toArray) {
      return address.toString();
    }

    let addressString = address.toArray().join(', ');
    // remove 2 commas
    addressString = addressString.replace(/,,/g, ',');
    // remove spaces after comma
    addressString = addressString.replace(/, /g, ',');
    // put only one space after comma
    addressString = addressString.replace(/,/g, ', ');
    return addressString;
  } catch (error) {
    logError(error);
    return '';
  }
};

// Function to break address lines into multiple lines
export function breakAddressLines(address: string, maxWidth = 45) {
  const lines = address.split('\n'); // Split the address into lines

  // Function to break a line into multiple lines if it's too long
  function breakLine(line) {
    const result = [];
    let currentLine = '';

    // Split the line into words and check if they fit within maxWidth
    const words = line.split(' ');
    for (const word of words) {
      if (currentLine.length + word.length + 1 <= maxWidth) {
        // Add the word to the current line
        if (currentLine.length > 0) {
          currentLine += ' ';
        }
        currentLine += word;
      } else {
        // Start a new line if the word doesn't fit
        result.push(currentLine);
        currentLine = word;
      }
    }

    // Add the last line
    if (currentLine.length > 0) {
      result.push(currentLine);
    }

    return result;
  }

  // Process each line in the address
  const processedLines = [];
  for (const line of lines) {
    if (line.length > maxWidth) {
      // If the line exceeds maxWidth, break it into multiple lines
      const brokenLines = breakLine(line);
      processedLines.push(...brokenLines);
    } else {
      // Otherwise, keep the line as it is
      processedLines.push(line);
    }
  }

  // Join the processed lines with line breaks
  return processedLines.join('\n');
}

export const convertAddressObjectToString = (address: GetAWorkplaceResponse['address']) => {
  if (!address) return '';

  let addressString = `${address.line1}`;

  if (address.line2) {
    addressString += `, ${address.line2}`;
  }

  addressString += `, ${address.city}, ${address.state} ${address.postalCode}`;

  return addressString;
};

/**
 * Builds the secondary text shown next to a workplace name in dropdowns.
 * Drops any address part already present in the workplace name so we don't render
 * the same value twice (e.g. a workplace named after its street: "4006 Darby Park Road").
 */
export const formatWorkplaceSecondaryAddress = (name: string, address: GetAWorkplaceResponse['address']) => {
  if (!address) return '';
  const norm = (value?: string) => (value || '').trim().toLowerCase();
  return [address.line1, address.city, address.state].filter((part) => part && norm(part) !== norm(name)).join(', ');
};

/**
 * A US address must always include a state. Treasury Prime (and other downstream KYB / payroll
 * integrations) reject a US business/person application whose state is empty, surfacing an opaque
 * "...'state' field cannot be empty when country is 'US'" error. Use this to block such a
 * submission on the client before it ever reaches the backend. (THERA-5723)
 *
 * Returns true when the country is US and the state is missing or blank.
 */
export const isUsStateMissing = (country?: string | null, state?: string | null): boolean =>
  country === 'US' && !state?.trim();

export const areAddressesSame = (address1: IAddress, address2: IAddress) => {
  if (!address1 || !address2) return undefined;

  const fieldsToCompare = ['country', 'city', 'postalCode', 'state', 'line1'];
  const line2Match = (!address1.line2 && !address2.line2) || address1.line2 === address2.line2;

  return fieldsToCompare.every((field) => address1[field] === address2[field]) && line2Match;
};
